import { Link } from 'react-router-dom';
import { Home } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/primitives';
import { useCompany } from '~/lib/queries';

function LegalShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10">
      <h1 className="app-page-title mb-6 text-2xl">{title}</h1>
      <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </div>
  );
}

export function LegalPage() {
  const { data: company } = useCompany();

  return (
    <LegalShell title="Mentions legales">
      <p className="whitespace-pre-line">
        {company?.legalNotice ??
          'Les mentions legales seront renseignees par l’administrateur depuis le back-office.'}
      </p>

      {company ? (
        <Card>
          <CardContent className="grid gap-1 pt-6 text-sm">
            <p className="font-medium text-foreground">{company.legalName}</p>
            <p>
              {company.street}, {company.postalCode} {company.city}
            </p>
            <p>Telephone : {company.phone}</p>
            <p>Courriel : {company.email}</p>
            {company.siret ? <p>SIRET : {company.siret}</p> : null}
            {company.vatNumber ? <p>TVA intracommunautaire : {company.vatNumber}</p> : null}
          </CardContent>
        </Card>
      ) : null}
    </LegalShell>
  );
}

/**
 * Information RGPD. Le sujet manipule des donnees sensibles — adresse du domicile,
 * geolocalisation, photographies prises chez le client : cette page n'est pas
 * optionnelle, elle fait partie des obligations du responsable de traitement.
 */
export function PrivacyPage() {
  return (
    <LegalShell title="Protection des donnees personnelles">
      <section className="space-y-2">
        <h2 className="font-semibold text-foreground">Donnees collectees</h2>
        <p>
          Identite et coordonnees, adresse d'intervention et ses coordonnees geographiques,
          caracteristiques du cycle, historique des interventions et photographies realisees avant
          et apres intervention.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold text-foreground">Finalites et base legale</h2>
        <p>
          Ces donnees sont traitees pour l'execution du contrat de prestation : planification des
          rendez-vous, realisation de l'intervention, facturation et suivi apres-vente. Aucune
          donnee n'est cedee ni revendue a un tiers.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold text-foreground">Durees de conservation</h2>
        <ul className="list-disc space-y-1 pl-5">
          <li>Compte client : trois ans apres la derniere intervention.</li>
          <li>Pieces comptables : dix ans, conformement au code de commerce.</li>
          <li>Photographies d'intervention : un an.</li>
          <li>Journal d'audit technique : un an (purge automatique).</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold text-foreground">Vos droits</h2>
        <p>
          Vous disposez d'un droit d'acces, de rectification, d'effacement, de limitation et de
          portabilite. Ces droits s'exercent depuis votre espace personnel ou par courriel. Vous
          pouvez introduire une reclamation aupres de la CNIL.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold text-foreground">Securite</h2>
        <p>
          Les mots de passe sont hashes avec argon2id. Les echanges sont chiffres en HTTPS. Les
          photographies sont stockees sur un serveur d'objets prive et ne sont accessibles que par
          des liens signes a duree limitee. L'acces aux donnees est cloisonne par role au niveau du
          serveur.
        </p>
      </section>
    </LegalShell>
  );
}

export function NotFoundContent() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
      <h1 className="app-page-title text-2xl">Page introuvable</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        L'adresse demandee n'existe pas ou a ete deplacee.
      </p>
      <Button asChild>
        <Link to="/">
          <Home aria-hidden />
          Retour a l'accueil
        </Link>
      </Button>
    </div>
  );
}
